import React, { useCallback, useMemo, useRef } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { TipTapEditor } from '../../TipTap';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useFormPalette } from './DynamicFormPrimitives';
import { makeDefaultField } from './fieldPreviews/fieldTypes';
import WysiwygFieldBlock from './fieldPreviews/WysiwygFieldBlock';
import RepeatableSectionBlock from './fieldPreviews/RepeatableSectionBlock';
import AddFieldButton from './fieldPreviews/AddFieldButton';

function FormHeader({ title, description, onTitleChange, onDescriptionChange, palette, extraFields, availablePlaceholders }) {
  return (
    <Box
      sx={{
        border: `1px solid ${palette.border}`,
        borderRadius: '10px',
        p: 1.25,
        backgroundColor: palette.surface,
        mb: '4px',
      }}
    >
      <Box sx={{ fontFamily: 'inherit', fontSize: '20px', fontWeight: 600, color: palette.textPrimary, mb: 0.5 }}>
        <TipTapEditor
          value={title || ''}
          onChange={onTitleChange}
          showToolbar={false}
          stripHtml={true}
          availablePlaceholders={availablePlaceholders}
          placeholder="Form title"
          minHeight="auto"
          className="tiptap-inline"
        />
      </Box>
      <Box sx={{ fontFamily: 'inherit', fontSize: '14px', color: palette.textSecondary }}>
        <TipTapEditor
          value={description || ''}
          onChange={onDescriptionChange}
          showToolbar={false}
          stripHtml={true}
          availablePlaceholders={availablePlaceholders}
          placeholder="Form description (optional)"
          minHeight="auto"
          className="tiptap-inline"
        />
      </Box>
      {extraFields}
    </Box>
  );
}

export default function DynamicFormFieldDesigner({
  fields,
  onChange,
  title,
  description,
  onTitleChange,
  onDescriptionChange,
  headerExtra,
  availablePlaceholders,
}) {
  const palette = useFormPalette();
  const theme = useTheme();
  const containerRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const sortableIds = useMemo(
    () => fields.map((f, i) => f._id || `field_${i}`),
    [fields]
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const findField = (id) => fields.findIndex((f, i) => (f._id || `field_${i}`) === id);
      const oldIndex = findField(active.id);
      const newIndex = findField(over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove([...fields], oldIndex, newIndex);
      reordered.forEach((f, i) => { f.order = i; });
      onChange(reordered);
    },
    [fields, onChange]
  );

  const addField = useCallback(
    (type, position) => {
      const newField = makeDefaultField(type, fields.length);
      if (position !== undefined && position >= 0 && position <= fields.length) {
        const updated = [...fields];
        updated.splice(position, 0, newField);
        updated.forEach((f, i) => { f.order = i; });
        onChange(updated);
      } else {
        onChange([...fields, newField]);
      }
    },
    [fields, onChange]
  );

  const updateField = useCallback(
    (index, updates) => {
      const updated = [...fields];
      updated[index] = { ...updated[index], ...updates };
      onChange(updated);
    },
    [fields, onChange]
  );

  const removeField = useCallback(
    (index) => {
      onChange(fields.filter((_, i) => i !== index));
    },
    [fields, onChange]
  );

  const duplicateField = useCallback(
    (index) => {
      const source = fields[index];
      const copy = { ...source, _id: crypto.randomUUID(), key: `${source.key}_copy_${Date.now()}`, order: fields.length };
      const updated = [...fields];
      updated.splice(index + 1, 0, copy);
      updated.forEach((f, i) => { f.order = i; });
      onChange(updated);
    },
    [fields, onChange]
  );

  const moveField = useCallback(
    (from, to) => {
      if (to < 0 || to >= fields.length) return;
      const reordered = arrayMove([...fields], from, to);
      reordered.forEach((f, i) => { f.order = i; });
      onChange(reordered);
    },
    [fields, onChange]
  );

  /* ─── Child field CRUD for repeatable sections ─── */

  const updateChildField = useCallback(
    (sectionIndex, childIndex, updates) => {
      const updated = [...fields];
      const section = { ...updated[sectionIndex] };
      const children = [...(section.children || [])];
      children[childIndex] = { ...children[childIndex], ...updates };
      section.children = children;
      updated[sectionIndex] = section;
      onChange(updated);
    },
    [fields, onChange]
  );

  const addChildField = useCallback(
    (sectionIndex, type, position) => {
      const updated = [...fields];
      const section = { ...updated[sectionIndex] };
      const children = [...(section.children || [])];
      const newChild = makeDefaultField(type, children.length);
      if (position !== undefined && position >= 0 && position <= children.length) {
        children.splice(position, 0, newChild);
      } else {
        children.push(newChild);
      }
      children.forEach((c, i) => { c.order = i; });
      section.children = children;
      updated[sectionIndex] = section;
      onChange(updated);
    },
    [fields, onChange]
  );

  const removeChildField = useCallback(
    (sectionIndex, childIndex) => {
      const updated = [...fields];
      const section = { ...updated[sectionIndex] };
      const children = (section.children || []).filter((_, i) => i !== childIndex);
      children.forEach((c, i) => { c.order = i; });
      section.children = children;
      updated[sectionIndex] = section;
      onChange(updated);
    },
    [fields, onChange]
  );

  const duplicateChildField = useCallback(
    (sectionIndex, childIndex) => {
      const updated = [...fields];
      const section = { ...updated[sectionIndex] };
      const children = [...(section.children || [])];
      const source = children[childIndex];
      const copy = { ...source, _id: crypto.randomUUID(), key: `${source.key}_copy_${Date.now()}`, order: children.length };
      children.splice(childIndex + 1, 0, copy);
      children.forEach((c, i) => { c.order = i; });
      section.children = children;
      updated[sectionIndex] = section;
      onChange(updated);
    },
    [fields, onChange]
  );

  const moveChildField = useCallback(
    (sectionIndex, from, to) => {
      const updated = [...fields];
      const section = { ...updated[sectionIndex] };
      const children = [...(section.children || [])];
      if (to < 0 || to >= children.length) return;
      const reordered = arrayMove(children, from, to);
      reordered.forEach((c, i) => { c.order = i; });
      section.children = reordered;
      updated[sectionIndex] = section;
      onChange(updated);
    },
    [fields, onChange]
  );

  const showHeader = onTitleChange || onDescriptionChange;

  return (
    <Box ref={containerRef} sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {showHeader && (
        <FormHeader
          title={title}
          description={description}
          onTitleChange={onTitleChange}
          onDescriptionChange={onDescriptionChange}
          palette={palette}
          extraFields={headerExtra}
          availablePlaceholders={availablePlaceholders}
        />
      )}

      {fields.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
            py: '20px',
            border: `2px dashed ${palette.border}`,
            borderRadius: '10px',
            backgroundColor: palette.surfaceSubtle,
          }}
        >
          <Typography sx={{ fontSize: '13px', color: palette.textSecondary }}>
            No fields yet
          </Typography>
          <AddFieldButton
            onSelect={addField}
            position={0}
            palette={palette}
            alwaysVisible
          />
        </Box>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {fields.map((field, idx) => {
                const stableId = field._id || `field_${idx}`;
                return field.type === 'repeatable_section' ? (
                  <RepeatableSectionBlock
                    key={stableId}
                    sortableId={stableId}
                    field={field}
                    index={idx}
                    total={fields.length}
                    onUpdate={updateField}
                    onRemove={removeField}
                    onMove={moveField}
                    onDuplicate={duplicateField}
                    onAddField={addField}
                    onUpdateChildField={updateChildField}
                    onAddChildField={addChildField}
                    onRemoveChildField={removeChildField}
                    onDuplicateChildField={duplicateChildField}
                    onMoveChildField={moveChildField}
                    theme={theme}
                    palette={palette}
                    availablePlaceholders={availablePlaceholders}
                  />
                ) : (
                  <WysiwygFieldBlock
                    key={stableId}
                    sortableId={stableId}
                    field={field}
                    index={idx}
                    total={fields.length}
                    onUpdate={updateField}
                    onRemove={removeField}
                    onMove={moveField}
                    onDuplicate={duplicateField}
                    onAddField={addField}
                    theme={theme}
                    palette={palette}
                    availablePlaceholders={availablePlaceholders}
                  />
                );
              })}
            </Box>
          </SortableContext>
        </DndContext>
      )}
    </Box>
  );
}
